import React, { useEffect, useMemo, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import ChatUtils from 'services/ChatUtils';
import ChatContext from 'contexts/ChatContext';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import ChatWindow from 'components/Chat/ChatWindow/index';
import useAssistant from 'hooks/useAssistant';
import { CAREY_ROOM_TYPE } from 'constants/carey';

const DEFAULT_ARRAY = [];

// If Carey hasn't replied within this window (e.g. the server restarted mid-reply),
// stop showing "typing…" and re-enable input so the patient can retry. Sending again
// re-triggers the reply with the full history, recovering the orphaned turn.
const CAREY_REPLY_TIMEOUT_MS = 90 * 1000;

const ChatRoomWindow = ({ bubble, patient, ...props }) => {
  const patientId = patient.id;

  const dispatch = useAppDispatch();
  const admins = useAppSelector((state) => state.admins.byId);
  const physicians = useAppSelector((state) => state.physicians.byId);
  const authentication = useAppSelector((state) => state.authentication);
  const archives = useAppSelector((state) => state.chat.archives[patientId]);
  const ownId = authentication.decodedToken.sub;

  const roomName = bubble?.room_name;

  const matchingArchive = useMemo(() => {
    return ChatUtils.getMatchingArchive(roomName, archives);
  }, [roomName, archives]);

  useEffect(() => {
    if (!matchingArchive) {
      dispatch(api.Chat.getUserArchives({ userId: patientId, role: 'patient' }));
    }
  }, [dispatch, matchingArchive, patientId]);

  const { streamingText, loadingReply, stopRequest, runSavedPromptTyped } = useAssistant(
    roomName,
    false,
  );

  const {
    markMessagesRead,
    unreadMessages,
    sendMessage,
    messages,
    careyActivity,
    clearCareyActivity,
    connected,
    onReconnect,
    connecting,
    joinedRooms,
  } = useContext(ChatContext);
  const careyStatus = careyActivity?.[roomName];
  let channelMessages = messages[roomName] || DEFAULT_ARRAY;
  channelMessages = useMemo(() => {
    return ChatUtils.getMergedMessages(roomName, channelMessages, archives);
  }, [roomName, channelMessages, archives]);

  const isChatRoom = !!bubble?.isLiveChat || !!roomName;

  const groupedMessages = useMemo(() => {
    return ChatUtils.getChatGroupedMessages(channelMessages, ownId, patient, admins, physicians);
  }, [channelMessages, ownId, patient, admins, physicians]);

  // In a Carey room, block sending a new message while we await Carey's reply: i.e. when the most
  // recent message was sent by the patient (and didn't fail). Carey's reply flips it back on. Scoped
  // to Carey rooms so human helpdesk chats still allow sending multiple messages back-to-back.
  const isCareyRoom = useMemo(
    () => (joinedRooms || []).some((r) => r.id === roomName && r.room_type === CAREY_ROOM_TYPE),
    [joinedRooms, roomName],
  );

  // Raw "awaiting reply": the latest message is the patient's and didn't fail.
  const { waiting, sinceTime } = useMemo(() => {
    if (!isCareyRoom) return { waiting: false, sinceTime: 0 };
    let last = null;
    for (const m of channelMessages) {
      if (!last || m.time >= last.time) last = m;
    }
    if (!last) return { waiting: false, sinceTime: 0 };
    const lastIsOwn = ChatUtils.isOwn(last.author ?? '', ownId);
    return {
      waiting: lastIsOwn && last.status !== 'failed',
      sinceTime: last.time || 0,
    };
  }, [isCareyRoom, channelMessages, ownId]);

  // Time out the wait so a dropped/crashed reply doesn't lock the UI forever.
  const [expired, setExpired] = useState(false);
  useEffect(() => {
    setExpired(false);
    if (!waiting) return undefined;
    // Carey never replied, so no reply will arrive to clear its "doing X…" status: drop it here or
    // the indicator lingers (and a stale label shows on the next turn).
    const onExpire = () => {
      setExpired(true);
      clearCareyActivity?.(roomName);
    };
    const remaining = CAREY_REPLY_TIMEOUT_MS - (Date.now() - sinceTime * 1000);
    if (remaining <= 0) {
      onExpire();
      return undefined;
    }
    const timer = setTimeout(onExpire, remaining);
    return () => clearTimeout(timer);
  }, [waiting, sinceTime, roomName, clearCareyActivity]);

  const waitingForCareyReply = waiting && !expired;

  return (
    <ChatWindow
      bubble={bubble}
      patient={patient}
      groupedMessages={groupedMessages}
      unreadMessages={unreadMessages}
      markMessagesRead={markMessagesRead}
      connected={connected}
      sendMessage={isChatRoom ? (message) => sendMessage(message, roomName) : undefined}
      onReconnect={onReconnect}
      connecting={connecting}
      streamingText={streamingText}
      stopRequest={stopRequest}
      loadingReply={loadingReply}
      runSavedPromptTyped={runSavedPromptTyped}
      {...props}
      disabledInput={props.disabledInput || waitingForCareyReply}
      careyTyping={waitingForCareyReply}
      careyStatus={careyStatus}
      careyTimedOut={waiting && expired}
      isCareyRoom={isCareyRoom}
    />
  );
};

ChatRoomWindow.propTypes = {
  patient: PropTypes.object,
  bubble: PropTypes.object,
  messages: PropTypes.object,
  disableSMS: PropTypes.bool,
  allowUpload: PropTypes.bool,
  renderTopBar: PropTypes.func,
  className: PropTypes.string,
  groupedMessages: PropTypes.array,
  form: PropTypes.string,
};

export default ChatRoomWindow;
